import { useState } from 'react'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { importacoesService, Monitor } from '@/services/importacoes-service'
import { apiErrorMessage } from '@/services/team-service'
import * as toast from '@/lib/toast'

interface ImportNowDialogProps {
  monitors: Monitor[]
  onStarted: () => void
}

/** "Importar agora": todos os monitoramentos ativos, uma OAB ou um processo; período opcional. */
export function ImportNowDialog({ monitors, onStarted }: ImportNowDialogProps) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState('TODOS')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [busy, setBusy] = useState(false)

  const start = async () => {
    setBusy(true)
    try {
      await importacoesService.startRun({
        escopo: target === 'TODOS' ? 'TODOS' : 'MONITORAMENTO',
        monitoramentoId: target === 'TODOS' ? undefined : target,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
      })
      toast.success('Importação iniciada', 'O resultado aparece na lista em instantes.')
      setOpen(false)
      onStarted()
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Não foi possível iniciar a importação'))
    } finally {
      setBusy(false)
    }
  }

  const oabs = monitors.filter((m) => m.tipo === 'OAB')
  const processes = monitors.filter((m) => m.tipo === 'PROCESSO')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Download /> Importar agora</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar agora</DialogTitle>
          <DialogDescription>Consulta o DJEN e grava só as publicações que ainda não existem.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="import-target">O que importar</Label>
            <NativeSelect id="import-target" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="TODOS">Todos os monitoramentos ativos</option>
              {oabs.length > 0 && (
                <optgroup label="Advogado (OAB)">
                  {oabs.map((m) => <option key={m.id} value={m.id}>{m.ownerName ? `${m.ownerName} — ${m.label}` : m.label}</option>)}
                </optgroup>
              )}
              {processes.length > 0 && (
                <optgroup label="Processo">
                  {processes.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </optgroup>
              )}
            </NativeSelect>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-inicio">De</Label>
              <Input id="import-inicio" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-fim">Até</Label>
              <Input id="import-fim" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Sem período, usa o padrão da API do DJEN (publicações recentes). Máximo de um ano.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={start} disabled={busy}>{busy ? 'Iniciando…' : 'Importar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
